import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxColor, OnyxOption } from '@onyx/angular';
import {
  AmountHelper,
  OnyxAddressComponent,
  OnyxDatePipe,
  OnyxDividerComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxLinkComponent,
  OnyxNamePipe,
  OnyxRouteLegType,
  OnyxToastService,
  OnyxToggleComponent,
  OnyxTooltipComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain, isEqual, isObject, omit, partition } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import { first, forkJoin, map, of } from 'rxjs';
import { validate as validateUuid } from 'uuid';
import { IntercomService } from '../../../../dashboard/common/services/intercom.service';
import { DelegationHelper } from '../../../../dashboard/delegations/common/helpers/delegation.helper';
import type { SimplifiedDelegation } from '../../../../dashboard/delegations/common/interfaces/delegation';
import { DelegationsService } from '../../../../dashboard/delegations/common/services/delegations.service';
import { DelegationActionModalMode } from '../../../../dashboard/delegations/delegation-action-modal/delegation-action-modal.component';
import { TechnicalDelegationPointName } from '../../../../dashboard/fleet/common/enums/technical-delegation-point-name';
import { TechnicalPointCategory } from '../../../../dashboard/fleet/common/enums/technical-point-category';
import { DelegationPointType } from '../../../../dashboard/fleet/common/enums/technical-point-type';
import { CompanyDictionaryCode } from '../../../../dashboard/management-panel/dictionaries/common/enums/company-dictionary-code';
import { CompanyDictionariesService } from '../../../../dashboard/management-panel/dictionaries/common/services/company-dictionaries.service';
import { TelematicsHelper } from '../../../../dashboard/map/common/helpers/telematics.helper';
import { OrderPointCategory } from '../../../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../../../dashboard/orders/common/enums/order-point-status';
import type { OrderLoadingPointGoodForm } from '../../../../dashboard/orders/common/helpers/order-form.helper';
import { OrderHelper } from '../../../../dashboard/orders/common/helpers/order.helper';
import type { OrderLoadingGood } from '../../../../dashboard/orders/common/interfaces/order-point';
import { OrderFormService } from '../../../../dashboard/orders/common/services/order-form.service';
import { DictionaryCode } from '../../../enums/dictionary-code';
import { RoutePointState } from '../../../enums/route/route-point-state';
import { CommonHelper } from '../../../helpers/common.helper';
import { ValidationHelper } from '../../../helpers/validation.helper';
import type { RouteSubpoint } from '../../../interfaces/route/route-subpoint';
import { DiffPipe } from '../../../pipes/diff.pipe';
import { DurationPipe } from '../../../pipes/duration.pipe';
import type { RoutePoint } from '../../../services/route.service';
import { RouteService } from '../../../services/route.service';
import { TelematicsStore } from '../../../stores/telematics.store';
import { RouteHelper } from './../../../helpers/route.helper';
import { RoutePointValueHelper } from '../../../helpers/route-point-value.helper';
import { RoutePointDetailsComponent } from './route-point-details/route-point-details.component';
import { RoutePointFormComponent } from './route-point-form/route-point-form.component';
import { RoutePointLegLineComponent } from './route-point-leg-line/route-point-leg-line.component';
import { RoutePointLegRideComponent } from './route-point-leg/route-point-leg-ride/route-point-leg-ride.component';
import { RoutePointLegComponent } from './route-point-leg/route-point-leg.component';
import { RoutePointMarkerComponent } from './route-point-marker/route-point-marker.component';
import type { RoutePointRouteSummary } from './route-point-route-summary/route-point-route-summary.component';
import { RoutePointRouteSummaryComponent } from './route-point-route-summary/route-point-route-summary.component';
import { RoutePointStatusFormComponent } from './route-point-status-form/route-point-status-form.component';
import { RoutePointTimeWindowsComponent } from './route-point-time-windows/route-point-time-windows.component';

@Component({
  selector: 'app-route-point',
  imports: [
    RoutePointMarkerComponent,
    OnyxIconComponent,
    OnyxAddressComponent,
    TranslatePipe,
    NgTemplateOutlet,
    OnyxToggleComponent,
    OnyxTooltipComponent,
    ReactiveFormsModule,
    RoutePointFormComponent,
    RoutePointDetailsComponent,
    AsyncPipe,
    OnyxTooltipDirective,
    RoutePointTimeWindowsComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    RoutePointStatusFormComponent,
    OnyxDatePipe,
    DurationPipe,
    RoutePointLegLineComponent,
    RoutePointLegComponent,
    OnyxLinkComponent,
    OnyxDividerComponent,
    RoutePointRouteSummaryComponent,
    RoutePointLegRideComponent,
  ],
  templateUrl: './route-point.component.html',
  styleUrl: './route-point.component.scss',
  host: {
    '[class.expanded]': `(headingExpanded() && (isPoint() || position().isFirst || position().isLast)) || isTooltip()`,
  },
})
export class RoutePointComponent implements OnInit {
  protected readonly telematicsStore = inject(TelematicsStore);
  private readonly routeService = inject(RouteService);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly diffPipe = inject(DiffPipe);
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly delegationsService = inject(DelegationsService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly commonHelper = inject(CommonHelper);
  private readonly intercomService = inject(IntercomService);
  private readonly translateService = inject(TranslateService);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly routeHelper = inject(RouteHelper);
  private readonly orderFormService = inject(OrderFormService, {
    optional: true,
  });

  public readonly point = input.required<RoutePoint>();
  public readonly letter = input<string | null>();
  public readonly variant = input<'component' | 'tooltip' | 'preview'>(
    'component',
  );
  public readonly previewColor = input<
    'transparent' | 'transparent-border' | 'gray'
  >('transparent-border');
  public readonly isFirstSubpoint = input(false);
  public readonly interactivePreview = input(false);
  public readonly forceIcons = input(false);
  public readonly ignoreEstimations = input(false);
  public readonly markerSize = input<24 | 32 | null>(null);
  public readonly markerColor = input<OnyxColor | null>(null);
  public readonly currentEndingDelegations = input<SimplifiedDelegation[]>([]);
  public readonly hideOptions = input(false);

  protected readonly I18N = 'route';
  protected readonly I18N_DELEGATION = `${this.I18N}.details.delegation`;

  protected readonly RoutePointState = RoutePointState;
  protected readonly OrderPointStatus = OrderPointStatus;
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly RouteHelper = RouteHelper;
  protected readonly RoutePointValueHelper = RoutePointValueHelper;
  protected readonly TechnicalPointCategory = TechnicalPointCategory;

  protected readonly context = this.routeService.context;
  protected readonly showEdit = this.routeService.showEdit;
  protected readonly showRemove = this.routeService.showRemove;
  protected readonly showCenter = this.routeService.showCenter;

  protected readonly calculatingRoute = this.orderFormService?.calculatingRoute;

  protected state = signal(RoutePointState.DEFAULT);
  protected actionsAttached = signal(false);
  protected subpointsExpanded = linkedSignal(() =>
    this.routeService.isPointExpanded(this.point()),
  );

  protected telematicsItem = computed(
    () =>
      this.routeService.telematicsItem() ??
      this.telematicsStore.getItem(this.routeService.vehicle()?.uuid),
  );
  protected offRouteLegIndex = computed(() => {
    const item = this.telematicsItem();
    if (!item || !this.routeService.isEqual(this.point(), item.currentPoint)) {
      return null;
    }

    return item.status.offRouteData?.legIndex ?? null;
  });
  protected headingExpanded = computed(() => {
    const isDefaultState = this.state() === RoutePointState.DEFAULT;
    const canExpandPreview =
      this.variant() !== 'preview' || this.interactivePreview();

    return (!isDefaultState && canExpandPreview) || this.isTooltip();
  });
  protected showFirstEmptyRoute = computed(() =>
    this.routeService.showFirstEmptyRoute(),
  );

  protected status = computed(
    () => RouteHelper.getStatus(this.point()) ?? OrderPointStatus.TO_DO,
  );
  protected compliance = computed(() =>
    RouteHelper.getScheduleCompliance(
      this.point(),
      this.routeService.points(),
      this.routeService,
      this.orderFormService,
    ),
  );
  protected estimations = computed(() =>
    !this.ignoreEstimations()
      ? RoutePointValueHelper.getEstimations(this.point())
      : null,
  );
  protected timestamps = computed(() =>
    RouteHelper.getTimestamps(this.point()),
  );
  protected isTodo = computed(() => this.status() === OrderPointStatus.TO_DO);
  protected isCanceled = computed(() => RouteHelper.isCanceled(this.point()));
  protected isCompleted = computed(() => RouteHelper.isCompleted(this.point()));
  protected completedAt = computed(() =>
    RouteHelper.getCompletedAt(this.point()),
  );
  protected showTimeWindows = computed(
    () =>
      this.isTodo() &&
      !this.estimations()?.arrivalTime &&
      !this.estimations()?.departureTime,
  );
  protected arrivalColor = computed(() =>
    RouteHelper.getStatusColor(
      this.point(),
      this.commonHelper.nowMinute(),
      'arrival',
      { skipWithoutTime: true },
    ),
  );
  protected departureColor = computed(() =>
    RouteHelper.getStatusColor(
      this.point(),
      this.commonHelper.nowMinute(),
      'departure',
      { skipWithoutTime: true },
    ),
  );
  protected hasArrivalTermConflict = computed(() =>
    RouteHelper.hasChainTermConflict(
      this.point(),
      this.routeService.points(),
      'arrival',
    ),
  );
  protected hasDepartureTermConflict = computed(() =>
    RouteHelper.hasChainTermConflict(
      this.point(),
      this.routeService.points(),
      'departure',
    ),
  );
  protected hasArrivalStatusIcons = computed(
    () =>
      !this.isCompleted() &&
      !this.isCanceled() &&
      (this.hasLoadedSemiTrailer() ||
        !!this.waitingForServiceDurationWarning() ||
        this.hasArrivalTermConflict()),
  );
  protected previousRoutePoint = computed(() => {
    const { index } = this.position();
    if (index === 0) return null;

    return this.routeService.points().at(index - 1) ?? null;
  });
  protected isDelegation = computed(() =>
    RouteHelper.isDelegationPoint(this.point()),
  );
  protected isMidCycleDelegationEnd = computed(() => {
    const point = this.point();
    if (!RouteHelper.isDelegationPoint(point, DelegationPointType.END)) {
      return false;
    }
    if (this.status() !== OrderPointStatus.COMPLETED) return false;

    return (
      RouteHelper.getDelegationData(point)?.isMidCycleDelegationEnd ?? false
    );
  });
  protected showCycleSeparator = computed(
    () =>
      RouteHelper.isDelegationPoint(this.point(), DelegationPointType.START) &&
      !this.position().isFirst,
  );
  protected cycleDiff = computed(() => {
    if (!this.showCycleSeparator()) return null;

    const value = this.value();
    if (!RouteHelper.isDelegationPoint(value, DelegationPointType.START)) {
      return null;
    }

    const previousRoutePoint = this.previousRoutePoint();
    if (!previousRoutePoint) return null;

    const previousPoint = RoutePointValueHelper.getValue(previousRoutePoint);
    if (
      !RouteHelper.isDelegationPoint(previousPoint, DelegationPointType.END)
    ) {
      return null;
    }

    const window = value.timeWindows.windows[0];
    const previousWindow = previousPoint.timeWindows.windows[0];
    if (
      !window?.date ||
      !window?.time ||
      !previousWindow?.date ||
      !previousWindow?.time
    ) {
      return null;
    }

    const diff = this.diffPipe.transform(
      `${window.date}T${window.time}`,
      `${previousWindow.date}T${previousWindow.time}`,
    );
    return diff.toMillis() > 0 ? diff : null;
  });

  protected hasMoveUp = computed(() =>
    this.routeService.hasMove(this.point(), -1),
  );
  protected hasMoveDown = computed(() =>
    this.routeService.hasMove(this.point(), 1),
  );
  protected hasEdit = computed(() => this.routeService.hasEdit(this.point()));
  protected hasRemove = computed(() =>
    this.routeService.hasRemove(this.point()),
  );
  protected options = computed<OnyxOption<() => void>[]>(() => {
    if (this.hideOptions()) return [];

    const point = this.point();
    const statusOptions: OnyxOption<() => void>[] = [];

    if (!('form' in point) && !RouteHelper.isDelegationPoint(point)) {
      const swapData = RouteHelper.getSwapData(point);
      const swapPoints =
        this.telematicsStore.swapPointsBySwapUuid().get(swapData?.uuid ?? '') ??
        null;
      const { disabled, tooltip } = RouteHelper.getChangeStatusState(
        point,
        this.telematicsItem(),
        this.routeService,
      );

      const loading = this.telematicsStore.loading();
      statusOptions.push({
        name: `${this.I18N}.changeStatus`,
        value: () =>
          this.routeService.togglePointState(point, RoutePointState.STATUS),
        leftIcon: { name: 'edit-outlined', size: 20 },
        leftIconColor: 'gray',
        spinner: loading,
        disabled: loading || disabled,
        tooltip: loading ? undefined : tooltip,
      });

      if (swapData) {
        const uuid = swapData.uuid;
        const hasCompletedSwap = RouteHelper.hasCompletedSwap(swapPoints);

        statusOptions.push({
          name: 'labels.revoke',
          value: () => this.routeHelper.cancelSwap(uuid),
          leftIcon: { name: 'clear-outlined', size: 20 },
          leftIconColor: 'red',
          spinner: loading,
          disabled: loading || hasCompletedSwap,
          tooltip:
            loading || !hasCompletedSwap
              ? undefined
              : `${this.I18N}.cancelImpossible`,
        });
      }
    }

    return [
      ...(this.showEdit()
        ? [
            {
              name: 'labels.edit',
              value: () => {
                const point = this.value();
                if (RouteHelper.isHandlingPoint(point)) {
                  this.routeService.edit$.next({
                    index: this.position().index,
                  });
                  return;
                }

                this.routeService.togglePointState(
                  this.point(),
                  RoutePointState.FORM,
                );
              },
              leftIcon: { name: 'edit-outlined', size: 20 },
              leftIconColor: 'gray',
            } as const,
          ]
        : []),
      ...statusOptions,
      ...(this.showRemove()
        ? [
            {
              name: 'labels.delete',
              value: () => this.removePoint(),
              leftIcon: { name: 'delete-outlined', size: 20 },
              leftIconColor: 'red',
              disabled: !this.hasRemove(),
            } as const,
          ]
        : []),
    ];
  });

  protected pointLegs = computed(() => {
    const point = this.point();
    const route = 'route' in point ? point.route : this.value().route;
    return route?.legs ?? [];
  });

  protected isEmpty = computed(() => this.routeService.isEmpty(this.point()));
  protected isReturningDelegationPoint = computed(() =>
    RouteHelper.isReturningDelegationPoint(this.point()),
  );
  protected returningLabels = computed<
    | {
        key: string;
        delegations: SimplifiedDelegation[];
        canReturn: boolean;
        params?: Record<string, string>;
      }[]
    | null
  >(() => {
    if (!this.isReturningDelegationPoint()) return null;

    const endingDelegations = this.currentEndingDelegations();
    if (!endingDelegations.length) return null;

    const getDriverNames = (delegation: SimplifiedDelegation) => {
      const drivers = [
        delegation.primaryDriver,
        delegation.secondaryDriver,
      ].filter((driver) => !!driver);

      return drivers
        .map((driver) => {
          const data = driver.driverData;
          return this.namePipe.transform(data, 'initialFirstName');
        })
        .join(', ');
    };

    if (endingDelegations.length === 1) {
      return [
        {
          key: `${this.I18N_DELEGATION}.driverIsEndingDelegation`,
          delegations: endingDelegations,
          canReturn: false,
        },
      ];
    }

    if (endingDelegations.length === 2) {
      const [returningDelegations, continuingDelegations] = partition(
        endingDelegations,
        ({ isReturning }) => isReturning,
      );

      if (returningDelegations.length === 2) {
        if (this.name() !== TechnicalDelegationPointName.DELEGATION_END) {
          return null;
        }

        return [
          {
            key: `${this.I18N_DELEGATION}.driversAreEndingDelegations`,
            delegations: returningDelegations,
            canReturn: false,
          },
        ];
      }

      if (
        [returningDelegations, continuingDelegations].every(
          ({ length }) => length === 1,
        )
      ) {
        return [
          {
            key: `${this.I18N_DELEGATION}.specifiedDriverIsEndingDelegation`,
            delegations: returningDelegations,
            canReturn: false,
            params: { driver: getDriverNames(returningDelegations[0]) },
          },
          {
            key: `${this.I18N_DELEGATION}.specifiedDriverIsContinuingDelegation`,
            delegations: continuingDelegations,
            canReturn: true,
            params: { driver: getDriverNames(continuingDelegations[0]) },
          },
        ];
      }
    }

    return null;
  });
  protected routeSummary = computed<RoutePointRouteSummary | null>(() => {
    const point = this.point();
    const route = 'route' in point ? point.route : this.value().route;
    if (!route?.summary) return null;

    const estimations = RoutePointValueHelper.getEstimations(point);
    const { index, isFirst } = this.position();

    let transitDuration: number | null = null;
    let remainingTransitDuration: number | null = null;

    if (estimations?.arrivalTime && !isFirst) {
      const previousPoint = this.routeService.points()[index - 1];
      const previousEstimations =
        RoutePointValueHelper.getEstimations(previousPoint);
      const previousTimestamps = RouteHelper.getTimestamps(previousPoint);

      const departure =
        previousTimestamps?.completedAt ?? previousEstimations?.departureTime;
      if (departure) {
        const departureTime = DateTime.fromISO(departure);
        const arrivalTime = DateTime.fromISO(estimations.arrivalTime);

        transitDuration = arrivalTime.diff(departureTime, 'seconds').seconds;
        remainingTransitDuration = arrivalTime.diff(
          this.commonHelper.nowMinute(),
          'seconds',
        ).seconds;
      }
    }

    return {
      ...route.summary,
      remainingDistance: estimations?.remainingDistance ?? null,
      transitTypes: chain(route.legs)
        .flatMap((leg) => leg.type)
        .filter((type) => type !== OnyxRouteLegType.VEHICLE)
        .value(),
      transitDuration,
      remainingTransitDuration,
    };
  });
  protected subpointsRouteSummary = computed<RoutePointRouteSummary | null>(
    () => {
      const routeSummary = this.routeSummary();
      if (!routeSummary) return null;

      const estimations = RoutePointValueHelper.getEstimations(this.point());
      const { index, isFirst } = this.position();

      let transitDuration: number | null = null;
      let remainingTransitDuration: number | null = null;

      if (estimations?.arrivalTime && !isFirst) {
        const previousIncludedIndex = index - this.subpoints().length;
        const previousIncludedPoint =
          this.routeService.points()[previousIncludedIndex];
        const previousIncludedEstimations =
          RoutePointValueHelper.getEstimations(previousIncludedPoint);

        if (previousIncludedEstimations?.departureTime) {
          const departureTime = DateTime.fromISO(
            previousIncludedEstimations.departureTime,
          );
          const arrivalTime = DateTime.fromISO(estimations.arrivalTime);

          transitDuration = arrivalTime.diff(departureTime, 'seconds').seconds;
          remainingTransitDuration = DateTime.min(
            arrivalTime,
            DateTime.max(this.commonHelper.nowMinute(), departureTime),
          ).diff(arrivalTime, 'seconds').seconds;
        }
      }

      return this.subpoints()
        .map(({ data }) => ({
          route:
            'route' in data
              ? data.route
              : RoutePointValueHelper.getValue(data).route,
          estimations: RoutePointValueHelper.getEstimations(data),
        }))
        .reduce((result, { route, estimations }) => {
          return {
            distance: result.distance + (route?.summary?.distance ?? 0),
            duration: result.duration + (route?.summary?.duration ?? 0),
            fuelConsumption:
              result.fuelConsumption + (route?.summary?.fuelConsumption ?? 0),
            totalCost: route?.summary?.totalCost
              ? AmountHelper.add(result.totalCost, route.summary.totalCost)
              : result.totalCost,
            remainingDistance: result.remainingDistance
              ? result.remainingDistance + (estimations?.remainingDistance ?? 0)
              : (estimations?.remainingDistance ?? null),
            transitTypes: chain(route?.legs ?? [])
              .flatMap((leg) => leg.type)
              .filter((type) => type !== OnyxRouteLegType.VEHICLE)
              .thru((transits) => [...result.transitTypes, ...transits])
              .uniq()
              .value(),
            transitDuration,
            remainingTransitDuration,
          };
        }, routeSummary);
    },
  );
  protected subpoints = computed(() => {
    if (!this.isPoint() && !this.position().isLast) return [];

    const points = this.routeService.points();
    const point = this.point();

    const index = this.routeService.getIndex(point);
    return chain(points)
      .slice(0, index)
      .thru((previousPoints) => {
        const previousIncludedIndex = previousPoints.findLastIndex(
          (point) =>
            RouteHelper.isIncludedInOrder(point) ||
            this.routeService.isFirst(point),
        );
        if (previousIncludedIndex === -1) return previousPoints;

        return previousPoints.slice(previousIncludedIndex + 1);
      })
      .map((data) => {
        return {
          data,
          icon: TelematicsHelper.getPointIcon(data),
        } as RouteSubpoint;
      })
      .value();
  });

  protected order = computed(() => {
    const point = this.point();
    return 'order' in point ? point.order : this.routeService.order();
  });
  protected goods$ = computed<Observable<OrderLoadingGood[] | null>>(() => {
    const point = this.value();
    if (!('goods' in point)) return of(null);

    let goods: (OrderLoadingGood | OrderLoadingPointGoodForm)[];
    if (point.category === OrderPointCategory.LOADING) {
      if ('status' in point) return of(point.goods);
      goods = point.goods;
    } else {
      const loadingGoods = this.routeService
        .points()
        .map((point) => RoutePointValueHelper.getValue(point))
        .filter((point) => point.category === OrderPointCategory.LOADING)
        .flatMap((point) => point.goods as typeof goods);

      goods = chain(point.goods)
        .map(({ uuid, quantity }) => {
          const good = loadingGoods.find((good) => good.uuid === uuid);
          if (!good) return null;

          return {
            ...good,
            quantity: quantity ?? 0,
            totalWeight: Math.floor(
              (good.totalWeight ?? 0) *
                ((quantity ?? 0) / (OrderHelper.getGoodQuantity(good) ?? 1)),
            ),
          };
        })
        .compact()
        .value();
    }

    if (
      goods.every(
        (good): good is OrderLoadingGood =>
          (isObject(good.type) || good.type == null) && isObject(good.unit),
      )
    ) {
      return of(goods);
    }

    const goodTypes$ =
      this.orderFormService?.goodTypes$ ??
      this.companyDictionariesService.listDictionary(
        CompanyDictionaryCode.GOOD_TYPE,
      );
    const units$ =
      this.orderFormService?.units$ ??
      this.companyDictionariesService.listDictionary(
        CompanyDictionaryCode.UNIT,
      );

    return forkJoin([goodTypes$.pipe(first()), units$.pipe(first())]).pipe(
      map(([goodTypes, units]) =>
        goods.map(
          (good) =>
            ({
              ...good,
              type: isObject(good.type)
                ? good.type
                : validateUuid(good.type ?? '')
                  ? (goodTypes.find(({ uuid }) => uuid === good.type) ?? null)
                  : null,
              typeName:
                'typeName' in good
                  ? good.typeName
                  : !validateUuid(good.type ?? '')
                    ? good.type
                    : null,
              unit: units.find(({ uuid }) => uuid === good.unit),
            }) as OrderLoadingGood,
        ),
      ),
    );
  });
  protected formRestorePoint = linkedSignal(() => {
    this.state();
    return untracked(() => omit(this.value(), 'route'));
  });
  protected formHasChanges = computed(
    () => !isEqual(omit(this.value(), 'route'), this.formRestorePoint()),
  );

  protected position = computed(() => ({
    index: this.routeService.getIndex(this.point()),
    isFirst: this.routeService.isFirst(this.point()),
    isLast: this.routeService.isLast(this.point()),
    isCurrent: this.routeService.isCurrent(this.point()),
  }));
  protected type = computed(() =>
    this.includedInOrder() ? 'point' : 'subpoint',
  );
  protected typeIndex = computed(() => {
    const point = this.point();
    if ('typeIndex' in point) return point.typeIndex + 1;

    if (!this.routeService.points().length) return null;

    const points = this.routeService.points().filter((point) => {
      if ('technicalPoint' in point && point.technicalPoint) return false;

      const pointType = 'point' in point ? point.point.type : point.type;
      return pointType === this.value().type;
    });

    return RouteHelper.getIndex(points, point) + 1;
  });
  protected icon = computed(
    () =>
      ({
        size:
          this.markerSize() ??
          (RoutePointValueHelper.isOrderFormPoint(this.value()) ||
          this.isPoint()
            ? 32
            : 24),
        name: TelematicsHelper.getPointIcon(this.value()),
      }) as const,
  );
  protected startAddress = computed(() => {
    const value = this.value();
    if ('startAddress' in value && value.startAddress) {
      return value.startAddress;
    }
    if ('address' in value && value.address) return value.address;
    return null;
  });

  protected hasLoadedSemiTrailer = computed(() => {
    const order = this.order() ?? this.routeService.order();
    const semiTrailer = order?.basicInformation?.loadedSemiTrailer;
    const uuid = RoutePointValueHelper.getValue(this.point()).uuid;
    if (!semiTrailer || !uuid) return false;

    return uuid === semiTrailer.pickupPointUuid;
  });
  protected waitingForServiceDurationWarning = computed(() =>
    RouteHelper.getWaitingForServiceDurationWarning(this.point()),
  );

  protected value = computed(() =>
    RoutePointValueHelper.getValue(this.point()),
  );
  protected name = computed(() => {
    const value = this.value();
    return 'name' in value ? value.name : null;
  });
  protected isTechnicalPoint = computed(() =>
    RouteHelper.isTechnicalPoint(this.point()),
  );
  protected form = computed(() => {
    const point = this.point();
    return 'form' in point ? point.form : null;
  });
  protected includedInOrder = computed(() =>
    RouteHelper.isIncludedInOrder(this.value()),
  );
  protected isTooltip = computed(() => this.variant() === 'tooltip');
  protected isPoint = computed(() => this.type() === 'point');

  public ngOnInit(): void {
    effect(
      () => {
        if (this.isTooltip()) return this.state.set(RoutePointState.DETAILS);

        const state = this.routeService.getPointState(this.point());
        if (this.form()?.invalid && state !== RoutePointState.FORM) {
          this.routeService.togglePointState(
            this.point(),
            RoutePointState.FORM,
          );
          return;
        }

        this.state.set(this.isTooltip() ? RoutePointState.DETAILS : state);
      },
      { injector: this.injector },
    );
  }

  protected async toggleDetails(): Promise<void> {
    const form = this.form();
    if (form && !(await ValidationHelper.checkValidity(form, this.injector))) {
      return;
    }

    this.routeService.togglePointState(this.point());
  }

  protected reportCalculateError(): void {
    const item = this.telematicsItem();
    const address = this.startAddress();

    const message = item
      ? this.translateService.instant(
          `${this.I18N}.calculateErrorIntercomMessage`,
          {
            registrationNumber: item.generalInformation.registrationNumber,
            vehicleUuid: item.uuid,
            address: address
              ? `${address.zipCode}, ${address.city}`
              : undefined,
            pointUuid: this.value().uuid,
          },
        )
      : undefined;

    this.intercomService.startConversation(message);
  }

  protected openActionModal(delegations: SimplifiedDelegation[]): void {
    const telematicsItem = this.telematicsItem();
    if (!telematicsItem) return;

    this.delegationHelper.openActionModal(
      DelegationActionModalMode.RETURN,
      delegations,
      telematicsItem,
    );
  }

  protected cancelReturn(delegations: SimplifiedDelegation[]): void {
    if (!delegations.length) return;

    const requests$ = delegations.map((delegation) =>
      this.delegationsService.cancelReturnDelegation(delegation.uuid),
    );

    forkJoin(requests$).subscribe(() =>
      this.toastService.showSuccess({
        message: 'delegations.delegationReturnCanceled',
      }),
    );
  }

  protected movePoint(direction: -1 | 1): void {
    this.routeService.move$.next({ index: this.position().index, direction });
  }

  protected centerPoint(): void {
    this.routeService.center$.next({
      id: this.value().uuid ?? this.position().index.toString(),
    });
  }

  protected removePoint(): void {
    this.routeService.remove$.next({ index: this.position().index });
  }

  protected undoFormChanges(): void {
    const form = this.form();
    if (form) form.patchValue(this.formRestorePoint() as any);
  }
}
